import { Box, Button, Text, VStack } from '@chakra-ui/react'
import { Handle, NodeProps, Position } from '@xyflow/react'
import React, { useMemo } from 'react'

import { useSwitchboardT } from '../../types'
import { text } from '../../utils/text'
import {
  InfoIconTooltip,
  nodeAddOptionButtonStyles,
  NodeCheckbox,
  NodeInput,
} from '../core'
import { BaseNode, nodePropsAreEqual } from './BaseNode'
import { useInteractiveWebhookNode } from './hooks/useInteractiveWebhookNode'

export interface InteractiveWebhookNodeProps extends NodeProps {
  onDelete?: () => void
  onDeleteNode?: (nodeId: string) => void

  showDeleteButton?: boolean
}

const InteractiveWebhookNodeComponent: React.FC<
  InteractiveWebhookNodeProps
> = ({ id, data, onDelete, onDeleteNode, showDeleteButton }) => {
  const {
    currentUrl,
    currentAllowExtensionAsOption,
    currentOptions,
    optionInputs,
    handleUrlChange,
    handleUrlBlur,
    handleAllowExtensionChange,
    handleAddOption,
    handleRemoveOption,
    handleOptionChange,
    handleOptionBlur,
    hasData,
  } = useInteractiveWebhookNode(id, data)
  const t = useSwitchboardT()

  // Top handles for "On Invalid" (top left) and "On Timeout" (top right)
  // Memoize handles array to prevent re-creating on every render
  const topHandles = useMemo(
    () => [
      {
        id: 'onInvalid',
        position: Position.Top as Position,
        style: {
          left: '0%',
          transform: 'translateX(-50%)',
        } as React.CSSProperties,
        label: text(t, 'switchboard.nodes.transfer.onInvalid', 'On Invalid'),
      },
      {
        id: 'onTimeout',
        position: Position.Top as Position,
        style: {
          left: '100%',
          transform: 'translateX(-50%)',
        } as React.CSSProperties,
        label: text(t, 'switchboard.nodes.transfer.onTimeout', 'On Timeout'),
      },
    ],
    [t],
  )

  return (
    <BaseNode
      label={text(
        t,
        'switchboard.nodeLabels.interactive_webhook',
        'INTERACTIVE WEBHOOK',
      )}
      showPlaceholder={!hasData}
      sourceHandlePosition={null}
      additionalSourceHandles={topHandles}
      onDelete={onDelete}
      onDeleteNode={onDeleteNode}
      id={id}
      showDeleteButton={showDeleteButton}
    >
      <VStack align="stretch" gap={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <InfoIconTooltip
            title={text(
              t,
              'switchboard.nodes.webhook.tooltipTitle',
              'What to do?',
            )}
            ariaLabel={text(
              t,
              'switchboard.nodes.interactiveWebhook.tooltipAria',
              'Interactive Webhook information',
            )}
            content={
              <VStack align="stretch" gap={2}>
                <Text wordBreak="break-word" overflowWrap="break-word">
                  {text(
                    t,
                    'switchboard.nodes.interactiveWebhook.tooltip.intro',
                    'The Interactive webhooks allow real-time routing between Mango and your software.',
                  )}
                </Text>
                <Box>
                  <Text fontWeight="semibold" mb={1} wordBreak="break-word">
                    {text(
                      t,
                      'switchboard.nodes.interactiveWebhook.tooltip.howItWorksHeading',
                      'How it works:',
                    )}
                  </Text>
                  <Text wordBreak="break-word" overflowWrap="break-word">
                    {text(
                      t,
                      'switchboard.nodes.interactiveWebhook.tooltip.howItWorksBody',
                      'Mango will make a GET request to the url defined. We will replace all {} variables with the actual data. You can use the parameters in the request to decide how to route the caller. This is done by sending back a response with the option you would like Mango to route the call to. This allows for realtime routing of the callers.',
                    )}
                  </Text>
                </Box>
                <Box>
                  <Text fontWeight="semibold" mb={1}>
                    {text(
                      t,
                      'switchboard.nodes.interactiveWebhook.tooltip.exampleCasesHeading',
                      'Example cases:',
                    )}
                  </Text>
                  <Text as="ul" pl={4} style={{ listStyleType: 'disc' }}>
                    <li>
                      {text(
                        t,
                        'switchboard.nodes.interactiveWebhook.tooltip.exampleCase1',
                        'Skills based routing',
                      )}
                    </li>
                    <li>
                      {text(
                        t,
                        'switchboard.nodes.interactiveWebhook.tooltip.exampleCase2',
                        'Route customers back to the agent that handled the initial call',
                      )}
                    </li>
                    <li>
                      {text(
                        t,
                        'switchboard.nodes.interactiveWebhook.tooltip.exampleCase3',
                        'Customize call route for returning callers.',
                      )}
                    </li>
                  </Text>
                </Box>
                <Box>
                  <Text fontWeight="semibold" mb={1}>
                    {text(
                      t,
                      'switchboard.nodes.interactiveWebhook.tooltip.exampleRequestHeading',
                      'Example request',
                    )}
                  </Text>
                  <Text
                    as="code"
                    fontSize="xs"
                    bg="bg.subtle"
                    p={2}
                    display="block"
                    borderRadius="sm"
                    whiteSpace="pre-wrap"
                    wordBreak="break-all"
                    overflowWrap="break-word"
                    maxW="100%"
                    style={{ wordWrap: 'break-word' }}
                  >
                    {`http://www.example.com/mango_notifications/authkey/?callerid_name={CALLER_ID}&callerid_number={CALLER_NUMBER}&dialed_number={NUMBER_DIALED}&dial_number_name={DIAL_NUMBER_NAME}&call_uid={CALL_UID}&time={TIME_UTC}`}
                  </Text>
                </Box>
                <Box>
                  <Text fontWeight="semibold" mb={1}>
                    {text(
                      t,
                      'switchboard.nodes.common.tooltip.variablesHeading',
                      'Variables',
                    )}
                  </Text>
                  <VStack align="stretch" gap={1} fontSize="xs">
                    <Text wordBreak="break-word" overflowWrap="break-word">
                      <Text as="strong">CALLER_ID</Text> -{' '}
                      {text(
                        t,
                        'switchboard.nodes.common.tooltip.callerIdDesc',
                        'The caller id name of the person calling',
                      )}
                    </Text>
                    <Text wordBreak="break-word" overflowWrap="break-word">
                      <Text as="strong">CALLER_NUMBER</Text> -{' '}
                      {text(
                        t,
                        'switchboard.nodes.common.tooltip.callerNumberDesc',
                        'The number of the person calling',
                      )}
                    </Text>
                    <Text wordBreak="break-word" overflowWrap="break-word">
                      <Text as="strong">NUMBER_DIALED</Text> -{' '}
                      {text(
                        t,
                        'switchboard.nodes.common.tooltip.numberDialedDesc',
                        'The number that was dialed',
                      )}
                    </Text>
                    <Text wordBreak="break-word" overflowWrap="break-word">
                      <Text as="strong">CALL_UID</Text> -{' '}
                      {text(
                        t,
                        'switchboard.nodes.common.tooltip.callUidDesc',
                        'The unique id of the call that can be used to lookup the call record at a later time.',
                      )}
                    </Text>
                    <Text wordBreak="break-word" overflowWrap="break-word">
                      <Text as="strong">DIALED_NUMBER_NAME</Text> -{' '}
                      {text(
                        t,
                        'switchboard.nodes.common.tooltip.dialedNumberNameDesc',
                        'The name in mango of the number being called',
                      )}
                    </Text>
                    <Text wordBreak="break-word" overflowWrap="break-word">
                      <Text as="strong">TIME_UTC</Text> -{' '}
                      {text(
                        t,
                        'switchboard.nodes.common.tooltip.timeUtcDesc',
                        'The time of the Call in UTC time zone',
                      )}
                    </Text>
                  </VStack>
                </Box>
                <Box>
                  <Text wordBreak="break-word" overflowWrap="break-word">
                    {text(
                      t,
                      'switchboard.nodes.interactiveWebhook.tooltip.responseNote',
                      'The option value in the response should correlate with options set on this object, you may also return an extension number.',
                    )}
                  </Text>
                </Box>
                <Box>
                  <Text fontWeight="semibold" mb={1}>
                    {text(
                      t,
                      'switchboard.nodes.interactiveWebhook.tooltip.exampleResponseHeading',
                      'Example Response',
                    )}
                  </Text>
                  <Text
                    as="code"
                    fontSize="xs"
                    bg="bg.subtle"
                    p={2}
                    display="block"
                    borderRadius="sm"
                    wordBreak="break-word"
                    overflowWrap="break-word"
                    maxW="100%"
                    style={{ wordWrap: 'break-word' }}
                  >
                    {text(
                      t,
                      'switchboard.nodes.interactiveWebhook.tooltip.exampleResponseBody',
                      '{"status": "success", "option": "1"}',
                    )}
                  </Text>
                </Box>
              </VStack>
            }
          />
          <Box flex={1}>
            <NodeInput
              label={text(t, 'switchboard.nodes.interactiveWebhook.url', 'URL')}
              value={currentUrl}
              onChange={(e) => handleUrlChange(e.target.value)}
              onBlur={handleUrlBlur}
              placeholder={text(
                t,
                'switchboard.nodes.interactiveWebhook.urlPlaceholder',
                'http://www.example.com/webhook',
              )}
            />
          </Box>
        </Box>

        <NodeCheckbox
          label={text(
            t,
            'switchboard.nodes.interactiveWebhook.allowExtensionAsOption',
            'Allow extension as option',
          )}
          checked={currentAllowExtensionAsOption}
          onChange={(e) => handleAllowExtensionChange(e.target.checked)}
        />

        <Box>
          <Button
            className="nodrag"
            {...nodeAddOptionButtonStyles}
            onClick={handleAddOption}
          >
            {text(t, 'switchboard.nodes.common.addOption', '+ Add Option')}
          </Button>
        </Box>

        {currentOptions.length > 0 && (
          <Box>
            <VStack align="stretch" gap={2}>
              {currentOptions.map((option, index) => (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  position="relative"
                  width="100%"
                >
                  {/* Handle positioned at node edge (like Call Distribution) */}
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`option-${index}`}
                    isConnectableEnd={false}
                    style={{
                      position: 'absolute',
                      right: '-24px', // Account for Card.Body padding (16px) + half handle width (8px)
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      // Colors handled by CSS for hover/highlight support
                    }}
                  />
                  <Button
                    className="nodrag"
                    size="xs"
                    variant="ghost"
                    colorPalette="neutral"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemoveOption(index)
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    minWidth="auto"
                    width="20px"
                    height="20px"
                    padding={0}
                    aria-label={text(
                      t,
                      'switchboard.nodes.common.removeOption',
                      'Remove option',
                    )}
                  >
                    ×
                  </Button>
                  <Box flex={1}>
                    <NodeInput
                      label={text(
                        t,
                        'switchboard.nodes.interactiveWebhook.option',
                        'Option',
                      )}
                      type="number"
                      value={optionInputs[index] || ''}
                      onChange={(e) =>
                        handleOptionChange(index, e.target.value)
                      }
                      onBlur={() => handleOptionBlur(index)}
                      placeholder={text(
                        t,
                        'switchboard.nodes.interactiveWebhook.optionPlaceholder',
                        'Option',
                      )}
                      min={0}
                      max={9999}
                      compact
                    />
                  </Box>
                </Box>
              ))}
            </VStack>
          </Box>
        )}
      </VStack>
    </BaseNode>
  )
}

export const InteractiveWebhookNode = React.memo(
  InteractiveWebhookNodeComponent,
  nodePropsAreEqual,
)
